import { baseURL } from "@/api/config";
import { useSessionStore } from "@/store/session";
import { type RemovableRef, useStorage } from "@vueuse/core";
import { type Ref, type UnwrapRef } from "vue";
import type {
	TCvCode,
	TCvExperience,
	TFile,
	TFileType,
	TPagination,
	TRole,
	TRouteName,
	TSubscriptionStatus,
	TTeacherProfile,
	TTranlsationLocales,
	TTranslation,
} from "./globalTypes";
import { useDefaultStore } from "@/store";
import { useToastStore } from "@/store/toast";
import { $t } from "@/i18n";

import IFlagPackFr from "~icons/flagpack/fr";
import IFlagPackEn from "~icons/flagpack/gb-ukm";
import type { PageState } from "primevue/paginator";
import type { TPaginationRequest } from "@/api/types";
import { DateInstance } from "./dateHelper";
import dayjs from "dayjs";

// #region Bounce / Throttle
export function debounce(func, waitTimer, immediate) {
	let timeout = null;
	return function () {
		// eslint-disable-next-line @typescript-eslint/no-this-alias, unicorn/no-this-assignment
		const context = this;
		// eslint-disable-next-line prefer-rest-params
		const args = arguments;
		const later = function () {
			timeout = null;
			if (!immediate) func.apply(context, args);
		};
		const callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, waitTimer);
		if (callNow) func.apply(context, args);
	};
}

export function throttle(func, waitTimer = 500) {
	let inThrottle = false;
	return function () {
		// eslint-disable-next-line prefer-rest-params
		const args = arguments;

		// eslint-disable-next-line @typescript-eslint/no-this-alias, unicorn/no-this-assignment
		const context = this;
		if (!inThrottle) {
			func.apply(context, args);
			inThrottle = true;
			setTimeout(() => {
				inThrottle = false;
			}, waitTimer);
		}
	};
}
// #endregion

// #region Simple crypt / decrypt
export function simpleCrypt(salt: string, text: string) {
	const textToChars = (text: string) =>
		text.split("").map((c) => c.codePointAt(0));
	const byteHex = (n: any) => ("0" + Number(n).toString(16)).slice(-2);
	const applySaltToChar = (code: any) =>
		textToChars(salt).reduce((a: any, b: any) => a ^ b, code);

	return text
		.split("")
		.map((element) => textToChars(element))
		.map((element) => applySaltToChar(element))
		.map((element) => byteHex(element))
		.join("");
}
export function simpleDecrypt(salt: string, encoded: string) {
	const textToChars = (text: any) =>
		text.split("").map((c: any) => c.codePointAt(0));
	const applySaltToChar = (code: any) =>
		textToChars(salt).reduce((a: any, b: any) => a ^ b, code);
	return encoded
		.match(/.{1,2}/g)
		.map((hex) => parseInt(hex, 16))
		.map((element) => applySaltToChar(element))
		.map((charCode) => String.fromCodePoint(charCode))
		.join("");
}
// #endregion

export function focusFirstElement(
	parentEl: HTMLElement,
	isFocus = true,
	isDebug = false,
) {
	// Don't invoke more than once (Before a component is destroyed)
	if (parentEl) {
		const inputListToIncludeFocusable = [
			"input:not(:disabled):not(.hidden)",
			"textarea:not(:disabled):not(.hidden)",
			"button:not(:disabled):not(.hidden)",
			"span.focusable",
			"div.focusable",
			".multiselect[tabindex]",
			"*[tabindex]:not(:disabled):not(.hidden)",
		].join(",");

		const nodeList = Array.from(
			parentEl.querySelectorAll(inputListToIncludeFocusable),
		) as HTMLElement[];
		if (nodeList?.length) {
			const addInputTabHandling = (nodeList: HTMLElement[]) => {
				const focusEl = (evt: KeyboardEvent, oppoEl: HTMLElement) => {
					// Only for first / last element
					oppoEl.focus();
					evt.preventDefault();
				};

				// First el
				nodeList[0].addEventListener("keydown", (evt: KeyboardEvent) => {
					if (evt.key === "Tab" && evt.shiftKey) {
						focusEl(evt, nodeList.at(-1) as any);
					}
				});

				// Last el
				(nodeList.at(-1) as any).addEventListener(
					"keydown",
					(evt: KeyboardEvent) => {
						if (evt.key === "Tab" && !evt.shiftKey) {
							focusEl(evt, nodeList[0]);
						}
					},
				);
			};

			if (isFocus) {
				// console.log("➕ Focusing first el", nodeList[0]);
				nodeList[0].focus();

				if (isDebug) {
					console.error("➕ Focusing first el", nodeList[0]);
				}
			}

			addInputTabHandling(nodeList);
		} else if (isDebug) {
			console.warn("No child element found for focus");
		}
	} else if (isDebug) {
		console.warn("No parent element found for focus");
	}
}

export function getCurrentDomain() {
	let url = new URL(location.href);
	try {
		url = new URL(baseURL);
	} catch {
		// ignored - could break on prod because link is not valid
	}
	return url;
}
function getUseStorage<T>(
	key: string,
	shouldParse = false,
	storage: Storage,
	defaultVal: T | null = null, // Undefined is broken (creates invalid value in store)
): Ref<T | UnwrapRef<T> | string | null | undefined> {
	const state = useStorage(key, defaultVal, storage);
	if (!shouldParse) return state;

	try {
		return ref(JSON.parse(state.value as string));
	} catch {
		console.error("Error loading key", key);
		return ref(defaultVal);
	}
}

export function getLocalStorageReac<T>(
	key: string,
	shouldParse = false,
	defaultVal?: T,
) {
	return getUseStorage(key, shouldParse, localStorage, defaultVal);
}

export function getSessionStorageReac<T>(
	key: string,
	shouldParse = false,
	defaultVal?: T,
) {
	return getUseStorage(key, shouldParse, sessionStorage, defaultVal);
}

export function setLocalStorageReac(
	key: string,
	value: any,
): RemovableRef<any> {
	let tempValue = value;
	if (typeof value !== "string") {
		tempValue = JSON.stringify(value);
	}

	// GH: https://github.com/vueuse/vueuse/issues/2193
	const state = useStorage(key, null, localStorage);
	state.value = tempValue;

	if (localStorage[key] !== tempValue) {
		localStorage[key] = tempValue;
		return ref(tempValue);
	}
	return state;
}

export function setSessionStorageReac(
	key: string,
	value: any,
): RemovableRef<any> {
	let tempValue = value;
	if (typeof value !== "string") {
		tempValue = JSON.stringify(value);
	}

	// GH: https://github.com/vueuse/vueuse/issues/2193
	const state = useStorage(key, null, sessionStorage);
	state.value = tempValue;

	if (sessionStorage[key] !== tempValue) {
		sessionStorage[key] = tempValue;
		return ref(tempValue);
	}
	return state;
}

export const InfoConsole = {
	/**
	 * Console log with colors
	 */
	l(msg: string, ...payload: any[]) {
		console.log(`:: %c${msg}`, "color:yellow; font-weight:bold", ...payload);
	},
	/**
	 * Console warn with colors
	 */
	w(msg: string, ...payload: any[]) {
		console.warn(`:: %c${msg}`, "color:yellow; font-weight:bold", ...payload);
	},
	/**
	 * Console error with colors
	 */
	e(msg: string, ...payload: any[]) {
		console.error(`:: %c${msg}`, "color:yellow; font-weight:bold", ...payload);
	},
};

export const EMAIL_REGEX =
	/(?:[a-z\d!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z\d!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\u0001-\u0008\u000B\u000C\u000E-\u001F!\u0023-\u005B\u005D-\u007F]|\\[\u0001-\u0009\u000B\u000C\u000E-\u007F])*")@(?:(?:[a-z\d](?:[a-z\d-]*[a-z\d])?\.)+[a-z\d](?:[a-z\d-]*[a-z\d])?|\[(?:(?:25[0-5]|2[0-4]\d|[01]?\d{1,2})\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d{1,2}|[a-z\d-]*[a-z\d]:(?:[\u0001-\u0008\u000B\u000C\u000E-\u001F\u0021-\u005A\u0053-\u007F]|\\[\u0001-\u0009\u000B\u000C\u000E-\u007F])+)])/;

export const TABLE_PT = {
	Column: {
		bodyCell: {
			class: ["p-table-body-cell"],
		},
	},
} as const;

export const FILE_PICKER_PT = {
	root: { class: ["p-custom-filepicker"] },
	pcMessage: {
		text: { class: ["p-custom-filepicker-message-text"] },
		content: { class: ["p-custom-filepicker-message-content"] },
		root: { class: ["p-custom-filepicker-message"] },
	},
	pcChooseButton: { root: { class: ["p-custom-filepicker-button"] } },
};

/**
 *
 * @param action async function to execute
 * @returns [execute, isLoading, error, data, message]
 */
export function useAsyncState(
	action: (...args: any) => Promise<any>,
): [
	(...args: any) => Promise<any>,
	Ref<boolean>,
	Ref<any>,
	Ref<any>,
	Ref<any>,
] {
	const isLoading = ref(false);
	const error = ref(null);
	const message = ref(null);
	const data = ref(null);

	const execute = async (...args) => {
		isLoading.value = true;
		error.value = null;
		try {
			const res = await action(...args);
			data.value = res.data;
			message.value = res.data.message;
			return res;
		} catch (err) {
			error.value = err;
			message.value = err.response?.data?.message || err.message;
			if (err.response?.status === 500) {
				useToastStore().openToastError($t("app.base.somethingWrong"));
			}
		} finally {
			isLoading.value = false;
		}
	};

	return [execute, isLoading, error, data, message];
}

export function useCan(store: ReturnType<typeof useSessionStore> = null) {
	if (!store) {
		store = useSessionStore();
	}
	return store.can;
}

export function getButtonSeverityFromStatus(status?: TSubscriptionStatus) {
	switch (status?.toLowerCase()) {
		case "active": {
			return "success";
		}
		case "canceled": {
			return "danger";
		}
		case "expired": {
			return "warning";
		}
		default: {
			return "secondary";
		}
	}
}
export function setupChangesFields<T>(
	original: T,
	change_ref: Ref<Partial<T>>,
): void;
export function setupChangesFields<T>(
	original: T[],
	change_ref: Ref<Partial<T>[]>,
): void;
export function setupChangesFields<T>(
	original: T | T[],
	change_ref: Ref<Partial<T>> | Ref<Partial<T>[]>,
): void {
	const originalCopy = deepToRaw(original);
	if (!Array.isArray(originalCopy)) {
		const keys = Object.keys(originalCopy);
		for (const el of keys) {
			if (Array.isArray(originalCopy[el])) {
				(change_ref as Ref<Partial<T>>).value[el] = originalCopy[el];
				continue;
			}
			if (
				typeof originalCopy[el] === "object" &&
				originalCopy[el] !== null &&
				!(originalCopy[el] instanceof Date)
			) {
				(change_ref as Ref<Partial<T>>).value[el] = originalCopy[el];
				continue;
			}
			(change_ref as Ref<Partial<T>>).value[el] = originalCopy[el];
		}
		return;
	}
	for (const el of originalCopy) {
		const keys = Object.keys(el);
		const obj: Partial<T> = {};
		for (const key of keys) {
			obj[key] = el[key];
		}
		(change_ref as Ref<Partial<T>[]>).value.push(obj);
	}
}

export function deepToRaw<T>(val: T): T {
	// eslint-disable-next-line unicorn/prefer-structured-clone
	return JSON.parse(JSON.stringify(val));
}

export function dynamicItemEmailValidator(email?: string) {
	return email && EMAIL_REGEX.test(email);
}

export function validateString(val: any, length = 255) {
	return (
		val && typeof val === "string" && val.trim() && val.trim().length <= length
	);
}
export function validateText(val: any, length = 3000) {
	return validateString(val, length);
}
export function validateNumber(number: any) {
	return (
		number === 0 || (number && !Array.isArray(number) && !isNaN(Number(number)))
	);
}

export const ROLES: TRole[] = [
	{
		rol_id: 1,
		rol_name: "super-admin",
		rol_label: "Super Admin",
	},
	{
		rol_id: 2,
		rol_name: "school-admin",
		rol_label: "School Admin",
	},
	{
		rol_id: 3,
		rol_name: "teacher",
		rol_label: "Teacher",
	},
] as const;

export function getBasePageUrlFromRole(
	rol_id: TRole["rol_id"],
	getBase = false,
): TRouteName {
	switch (rol_id) {
		case 1: {
			return "admin";
		}
		case 2: {
			return "school";
		}
		case 3: {
			if (getBase) {
				return "teacher";
			}
			const sessionStore = useSessionStore();
			return sessionStore.userData.accountapproved
				? "teacher.dashboard"
				: "teacher.profile";
		}
		default: {
			return "auth";
		}
	}
}

export function useCurrency(store: ReturnType<typeof useDefaultStore> = null) {
	if (!store) {
		store = useDefaultStore();
	}
	return { symbol: store.currencySymbol, iso: store.currencyIso };
}

// deep compare object
export function deepEqual(a: any, b: any): boolean {
	if (!a && !b) return true;
	if (!a || !b) return false;
	if (typeof a !== "object" || typeof b !== "object") {
		return a === b;
	}
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);
	if (keysA.length !== keysB.length) return false;
	for (const key of keysA) {
		if (!deepEqual(a[key], b[key])) return false;
	}
	return true;
}

export function setupTranslationObject<T extends Record<string, unknown>>(
	value: T,
	locales: TTranlsationLocales[] = ["fr"],
): TTranslation<T> {
	const obj: TTranslation<T> = {};
	for (const locale of locales) {
		obj[locale] = { ...value };
	}
	return obj;
}

export function getNewTranslationObject<T extends Record<string, unknown>>(
	locales: TTranlsationLocales[] = ["fr"],
	keys: (keyof T)[],
): TTranslation<T> {
	const obj = {};
	for (const locale of locales) {
		obj[locale] = {} as T;
		for (const el of keys) {
			obj[locale][el] = "";
		}
	}
	return obj as TTranslation<T>;
}

export function getFlagForLocale(locale?: TTranlsationLocales) {
	switch (locale) {
		case "fr": {
			return IFlagPackFr;
		}
		default: {
			return IFlagPackEn;
		}
	}
}

// used for trimming unused translation keys
export function filterEmptyTranlations<T extends Record<string, unknown>>(
	translation: TTranslation<T>,
): Partial<TTranslation<T>> {
	const obj = {} as Partial<TTranslation<T>>;
	for (const key in translation) {
		obj[key] = {};
		for (const subkey in translation[key]) {
			if (translation[key][subkey]) {
				obj[key] = translation[key];
			}
		}
	}
	return obj;
}

export class Errors {
	private errors: Map<string, string[]>;
	constructor(errors: { [key: string]: any } = {}) {
		this.errors = new Map(Object.entries(errors));
	}

	get(key: string) {
		return this.errors.get(key);
	}
	has(key: string) {
		return this.errors.has(key);
	}

	all() {
		return this.errors.entries();
	}

	any() {
		return this.errors.size > 0;
	}

	clear(): void;
	clear(field?: string): void {
		if (field) {
			this.errors.delete(field);
			return;
		}
		this.errors.clear();
	}

	record(key: string, val: any) {
		this.errors.set(key, val);
	}
}

export function formatPagination(params: PageState): Partial<TPagination> {
	return {
		current_page: params.page + 1,
		per_page: params.rows,
	};
}

export function updatePagination(
	pagination: Ref<TPagination | null>,
	params: Partial<TPagination>,
) {
	if (!pagination.value) {
		pagination.value = {
			total: 0,
			count: 0,
			per_page: 10,
			current_page: 1,
			offset: 0,
			limit: 10,
			total_pages: 0,
		};
	}
	for (const key of Object.keys(params)) {
		pagination.value[key] = params[key];
	}
}

export function getRequestPagination(
	params: TPagination,
	defaultRows: number = 15,
): TPaginationRequest {
	if (!params) {
		return {
			page: 1,
			per_page: defaultRows,
		};
	}
	return {
		page: params.current_page,
		per_page: params.per_page,
	};
}

export function handleNewExperiences(exps: TCvExperience[]) {
	const newExps = exps.map((exp) => {
		const newExp = { ...exp };
		if (newExp.cve_id < 0) {
			delete newExp.cve_id;
		}
		return newExp;
	});
	return newExps;
}

export const PHONE_KEYFILTER = {
	pattern: /^\+?(\d*)?$/,
	validateOnly: true,
};

export function validatePassword(password: string) {
	const re =
		/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!.%*?&])[A-Za-z\d@$!.%*?&]{8,}$/;
	return re.test(password);
}

export function useIsMobile(store?: ReturnType<typeof useDefaultStore>) {
	if (!store) {
		store = useDefaultStore();
	}
	return computed(() => store.isMobileSize);
}

export const defaultNumberLocale = "fr-FR";

export const remoteWorkOptions = computed(() => [
	{ value: 1, label: $t("app.base.yes") },
	{ value: 0, label: $t("app.base.no") },
]);

export const yearsOfExperience = computed(() => [
	{
		value: 1,
		label: $t("app.experience.1"),
	},
	{
		value: 2,
		label: $t("app.experience.2"),
	},
	{
		value: 3,
		label: $t("app.experience.3"),
	},
	{
		value: 4,
		label: $t("app.experience.4"),
	},
]);

/**
 * Compare two arrays for equality.
 * The arrays are considered equal if they have the same length and each corresponding
 * element is equal. For primitives, strict equality is used.
 * For objects, if a compareKey is provided then the objects are compared by their key value.
 *
 * @param arr1 - The first array.
 * @param arr2 - The second array.
 * @param key - Optional key to use for comparing objects.
 * @returns True if the arrays are equal, otherwise false.
 */
export function compareArrays<T>(arr1: T[], arr2: T[], key?: keyof T): boolean {
	if (arr1.length !== arr2.length) return false;
	return arr1.every((item, index) => {
		const current = arr2[index];
		if (
			key &&
			typeof item === "object" &&
			item !== null &&
			typeof current === "object" &&
			current !== null
		) {
			return item[key] === current[key];
		}
		return item === current;
	});
}

export const COURSE_LEVELS = computed(() => [
	{
		value: "MIDDLE",
		label: $t("app.courseLevels.middle"),
	},
	{
		value: "HIGH",
		label: $t("app.courseLevels.high"),
	},
	{
		value: "BACHELOR",
		label: $t("app.courseLevels.bachelor"),
	},
	{
		value: "MASTER",
		label: $t("app.courseLevels.master"),
	},
	{
		value: "ABOVE_MASTER",
		label: $t("app.courseLevels.above_master"),
	},
]);

export function humanReadableSize(size?: number) {
	if (!size) return "0";
	const i = Math.floor(Math.log(size) / Math.log(1024));
	const sizes = ["B", "KB", "MB", "GB", "TB"];
	return `${(size / Math.pow(1024, i)).toFixed(2)}${sizes[i]}`;
}

export function shortenString(string: string, length = 15) {
	if (string.length <= length) return string;
	return `${string.slice(0, length)}...`;
}

export function validateExperience(item?: TCvExperience) {
	if (!item) return false;
	switch (item.cvt_code) {
		case "EDUC": {
			return validateEducationExperience(item);
		}
		case "TEXP": {
			return validateWorkingExperience(item);
		}
		case "PEXP": {
			return validateProfessionalExperiece(item);
		}
	}
}

function validateDate(date: string) {
	return date && DateInstance.isValidDate(date);
}

export function validateEducationExperience(item: TCvExperience) {
	return (
		validateString(item.cve_title) &&
		validateString(item.cve_company) &&
		validateDate(item.cve_start) &&
		(!item.cve_end || validateDate(item.cve_end))
	);
}

export function validateWorkingExperience(item: TCvExperience) {
	return (
		validateEducationExperience(item) &&
		(!item.cve_description || validateString(item.cve_description))
	);
}

export function validateProfessionalExperiece(item: TCvExperience) {
	return (
		validateWorkingExperience(item) &&
		// validateString(item.cve_coursecontent) &&
		validateString(item.cve_coursetitle) &&
		item.cve_courselevel &&
		COURSE_LEVELS.value.some((cl) => cl.value === item.cve_courselevel)
	);
}
export function isEmptyExperience(item: TCvExperience) {
	return Object.keys(item).every((key) => !item[key]);
}

export function validateDocuments(files: TFile[], document_types: TFileType[]) {
	const missing_types = [];
	for (const type of document_types) {
		const file = files.find((f) => f.ftp_code === type.ftp_code);
		if (!file && !type.drq_optional) missing_types.push(type);
	}
	return missing_types;
}

const TEACHER_SETUP_KEY_ORDER: (keyof TTeacherProfile | TCvCode)[] = [
	"cities",
	"usr_avatar",
	"languages",
	"usr_phone",
	"usr_title",
	"usr_hourlyrate",
	"usr_remotework",
	"contract_types",
	"usr_experience",
	"usr_professionalexperience",
	"EDUC",
	"PEXP",
	"TEXP",
	"files",
];

const TEACHER_SETUP_ROUTE_GETTER = (
	val: keyof TTeacherProfile | TCvCode,
): TRouteName => {
	switch (val) {
		case "cities":
		case "usr_avatar":
		case "languages":
		case "usr_phone":
		case "usr_title": {
			return "teacher.profile.general-info";
		}
		case "usr_hourlyrate":
		case "usr_remotework":
		case "contract_types":
		case "usr_experience":
		case "usr_professionalexperience":
		case "subjects": {
			return "teacher.profile.teaching-info";
		}
		case "EDUC": {
			return "teacher.profile.education-background";
		}
		case "PEXP": {
			return "teacher.profile.working-experience";
		}
		case "TEXP": {
			return "teacher.profile.teaching-experience";
		}
		case "files": {
			return "teacher.profile.documents";
		}
		default: {
			return "teacher.profile.general-info";
		}
	}
};

export function getProfileSetupErrorNextPage(errors: Errors): TRouteName {
	for (const el of TEACHER_SETUP_KEY_ORDER) {
		if (errors.has(el)) {
			return TEACHER_SETUP_ROUTE_GETTER(el);
		}
	}
	return "teacher.profile.general-info";
}
export async function downloadFile(apiResponse: Blob, name: string) {
	// const blob = new Blob([apiResponse]);
	const link = document.createElement("a");
	link.href = URL.createObjectURL(apiResponse);
	link.download = name;
	document.body.append(link);
	link.click();
	link.remove();
}

// checks teacher booking for timeslot and availability
export function timeslotNotAvailable(
	bookings: { cls_start: string; cls_end: string; cls_id: number }[],
	availability: { avl_start: string; avl_end: string }[],
	start: Date,
	end: Date,
	cls_id: number = -1,
) {
	for (const booking of bookings) {
		if (
			cls_id !== booking.cls_id &&
			dayjs(start).isBefore(booking.cls_end) &&
			dayjs(booking.cls_start).isBefore(end)
		) {
			return true;
		}
	}

	// for (const avl of availability) {
	// 	if (
	// 		dayjs(start).isAfter(avl.avl_start) &&
	// 		dayjs(end).isBefore(avl.avl_end)
	// 	) {
	// 		return false;
	// 	}
	// }

	for (const avl of availability) {
		if (
			dayjs(start).isSameOrAfter(avl.avl_start) &&
			dayjs(end).isSameOrBefore(avl.avl_end)
		) {
			return false;
		}
	}

	return true;
}

export function formatTimeFromMinutes(
	totalMinutes: number,
	withDays = true,
): string {
	const days = Math.floor(totalMinutes / (60 * 24));
	const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
	const minutes = totalMinutes % 60;

	const parts = [];

	if (withDays) {
		if (days > 0) parts.push(`${days}d`);
		if (hours > 0) parts.push(`${hours}h`);
	} else {
		const totalHours = Math.floor(totalMinutes / 60);
		if (totalHours > 0) parts.push(`${totalHours}h`);
	}

	if (minutes > 0 || parts.length === 0) parts.push(`${minutes}min`);

	return parts.join(" ");
}

export function formatDateOrOngoing(date: string) {
	if (dayjs(date).format("YYYY-MM-DD") === "3000-01-01") {
		return $t("app.base.ongoing");
	}
	return dayjs(date).format("MMMM YYYY");
}
